import XLSX from 'xlsx-js-style';

import type {
  XlsxCellKey,
  XlsxParsedBorderSide,
  XlsxParsedBorders,
  XlsxParsedCellStyle,
  XlsxParsedColumnStyle,
} from './types';
import { firstElementBody, parseXmlAttributes } from './xml';

interface RawBorderSide {
  style: string;
  color?: Record<string, unknown>;
}

export interface RawBorder {
  top?: RawBorderSide;
  bottom?: RawBorderSide;
  left?: RawBorderSide;
  right?: RawBorderSide;
}

export interface ParsedStyleReferences {
  cells: Map<XlsxCellKey, number>;
  rows: Map<number, number>;
  columns: Array<{ min: number; max: number; styleIndex: number }>;
}

export type ParsedCellXfStyleSupplements = readonly XlsxParsedCellStyle[];

const BUILTIN_NUM_FMTS: Record<number, string> = {
  1: '0', 2: '0.00', 3: '#,##0', 4: '#,##0.00',
  9: '0%', 10: '0.00%', 11: '0.00E+00', 12: '# ?/?', 13: '# ??/??',
  14: 'm/d/yy', 15: 'd-mmm-yy', 16: 'd-mmm', 17: 'mmm-yy',
  18: 'h:mm AM/PM', 19: 'h:mm:ss AM/PM', 20: 'h:mm', 21: 'h:mm:ss',
  22: 'm/d/yy h:mm', 37: '#,##0 ;(#,##0)', 38: '#,##0 ;[Red](#,##0)',
  39: '#,##0.00;(#,##0.00)', 40: '#,##0.00;[Red](#,##0.00)',
  41: '_(* #,##0_);_(* (#,##0);_(* "-"_);_(@_)',
  42: '_("$"* #,##0_);_("$"* (#,##0);_("$"* "-"_);_(@_)',
  43: '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)',
  44: '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)',
  45: 'mm:ss', 46: '[h]:mm:ss', 47: 'mmss.0', 48: '##0.0E+0', 49: '@',
};

const THEME_COLORS: Record<number, string> = {
  0: '#000000', 1: '#FFFFFF', 2: '#44546A', 3: '#4472C4',
  4: '#ED7D31', 5: '#A5A5A5', 6: '#FFC000', 7: '#5B9BD5',
  8: '#70AD47', 9: '#264478',
};

const INDEXED_COLORS: Record<number, string> = {
  0: '#000000', 1: '#FFFFFF', 2: '#FF0000', 3: '#00FF00',
  4: '#0000FF', 5: '#FFFF00', 6: '#FF00FF', 7: '#00FFFF',
  8: '#000000', 9: '#FFFFFF', 10: '#FF0000', 11: '#00FF00',
  12: '#0000FF', 13: '#FFFF00', 14: '#FF00FF', 15: '#00FFFF',
  16: '#800000', 17: '#008000', 18: '#000080', 19: '#808000',
  20: '#800080', 21: '#008080', 22: '#C0C0C0', 23: '#808080',
  64: '#000000',
};

function normalizeRgb(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const rgb = value.replace(/^#/, '').trim().toUpperCase();
  if (/^[0-9A-F]{8}$/.test(rgb)) return `#${rgb.slice(2)}`;
  if (/^[0-9A-F]{6}$/.test(rgb)) return `#${rgb}`;
  return undefined;
}

export function xlsxColorToHex(color: unknown): string | undefined {
  if (!color || typeof color !== 'object') return undefined;
  const value = color as Record<string, unknown>;
  const rgb = normalizeRgb(value.rgb);
  if (rgb) return rgb;
  if (typeof value.theme === 'number') return THEME_COLORS[value.theme];
  if (typeof value.theme === 'string') return THEME_COLORS[parseInt(value.theme, 10)];
  if (typeof value.indexed === 'number') return INDEXED_COLORS[value.indexed];
  if (typeof value.indexed === 'string') return INDEXED_COLORS[parseInt(value.indexed, 10)];
  return undefined;
}

function parseColorTag(source: string): Record<string, unknown> | undefined {
  const match = /<(?:\w+:)?color\b([^>]*?)\/?\s*>/i.exec(source);
  if (!match) return undefined;
  const attrs = parseXmlAttributes(match[1]);
  const color: Record<string, unknown> = {};
  if (attrs.rgb) color.rgb = attrs.rgb;
  if (attrs.theme !== undefined) color.theme = parseInt(attrs.theme, 10);
  if (attrs.indexed !== undefined) color.indexed = parseInt(attrs.indexed, 10);
  if (attrs.tint !== undefined) color.tint = parseFloat(attrs.tint);
  if (attrs.auto !== undefined) color.auto = attrs.auto === '1' || attrs.auto === 'true';
  return Object.keys(color).length > 0 ? color : undefined;
}

export function parseBordersFromStylesXml(stylesXml: string): RawBorder[] {
  const block = firstElementBody(stylesXml, 'borders');
  if (block == null) return [];
  const borders: RawBorder[] = [];
  const borderRe = /<(?:\w+:)?border\b[^>]*?(?:\/\s*>|>([\s\S]*?)<\/(?:\w+:)?border\s*>)/gi;
  let match: RegExpExecArray | null;
  while ((match = borderRe.exec(block)) !== null) {
    const body = match[1] ?? '';
    const border: RawBorder = {};
    for (const sideName of ['left', 'right', 'top', 'bottom'] as const) {
      const sideRe = new RegExp(
        `<(?:\\w+:)?${sideName}\\b([^>]*?)(?:\\/\\s*>|>([\\s\\S]*?)<\\/(?:\\w+:)?${sideName}\\s*>)`,
        'i',
      );
      const sideMatch = sideRe.exec(body);
      if (!sideMatch) continue;
      const attrs = parseXmlAttributes(sideMatch[1] ?? '');
      if (!attrs.style) continue;
      const side: RawBorderSide = { style: attrs.style };
      const color = parseColorTag(sideMatch[2] ?? '');
      if (color) side.color = color;
      border[sideName] = side;
    }
    borders.push(border);
  }
  return borders;
}

export function parseStyleReferences(sheetXml: string): ParsedStyleReferences {
  const cells = new Map<XlsxCellKey, number>();
  const rows = new Map<number, number>();
  const columns: Array<{ min: number; max: number; styleIndex: number }> = [];

  const cellRe = /<(?:\w+:)?c\b([^>]*)>/gi;
  let match: RegExpExecArray | null;
  while ((match = cellRe.exec(sheetXml)) !== null) {
    const attrs = parseXmlAttributes(match[1]);
    if (!attrs.r || attrs.s === undefined) continue;
    try {
      const decoded = XLSX.utils.decode_cell(attrs.r.replace(/\$/g, '').toUpperCase());
      cells.set(`${decoded.r}:${decoded.c}`, parseInt(attrs.s, 10));
    } catch {
      // Preflight reports invalid materialized cells; ignore malformed optional style refs here.
    }
  }

  const rowRe = /<(?:\w+:)?row\b([^>]*)>/gi;
  while ((match = rowRe.exec(sheetXml)) !== null) {
    const attrs = parseXmlAttributes(match[1]);
    if (!attrs.r || attrs.s === undefined) continue;
    if (attrs.customFormat !== '1' && attrs.customFormat !== 'true') continue;
    const row = parseInt(attrs.r, 10) - 1;
    const styleIndex = parseInt(attrs.s, 10);
    if (row >= 0 && Number.isFinite(styleIndex)) rows.set(row, styleIndex);
  }

  const colRe = /<(?:\w+:)?col\b([^>]*)\/?\s*>/gi;
  while ((match = colRe.exec(sheetXml)) !== null) {
    const attrs = parseXmlAttributes(match[1]);
    if (attrs.style === undefined || attrs.min === undefined || attrs.max === undefined) continue;
    const min = parseInt(attrs.min, 10) - 1;
    const max = parseInt(attrs.max, 10) - 1;
    const styleIndex = parseInt(attrs.style, 10);
    if (min < 0 || max < min || !Number.isFinite(styleIndex) || styleIndex === 0) continue;
    columns.push({ min, max, styleIndex });
  }
  return { cells, rows, columns };
}

function parsedToggle(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return undefined;
}

function parsedFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function applyParsedAlignment(
  alignment: Record<string, unknown> | undefined,
  result: XlsxParsedCellStyle,
): void {
  if (!alignment) return;
  if (
    alignment.horizontal === 'left'
    || alignment.horizontal === 'center'
    || alignment.horizontal === 'right'
  ) {
    result.align = alignment.horizontal;
  }
  if (alignment.vertical === 'top') result.verticalAlign = 'top';
  else if (alignment.vertical === 'center') result.verticalAlign = 'middle';
  else if (alignment.vertical === 'bottom') result.verticalAlign = 'bottom';

  const wrapText = parsedToggle(alignment.wrapText ?? alignment.wraptext);
  if (wrapText !== undefined) result.wrapText = wrapText;
  const shrinkToFit = parsedToggle(alignment.shrinkToFit ?? alignment.shrinktofit);
  if (shrinkToFit !== undefined) result.shrinkToFit = shrinkToFit;

  const textRotation = parsedFiniteNumber(alignment.textRotation ?? alignment.textrotation);
  if (textRotation !== undefined && textRotation >= 0 && textRotation <= 255) {
    result.textRotation = textRotation;
  }
  const indent = parsedFiniteNumber(alignment.indent);
  if (indent !== undefined && indent >= 0) result.indent = indent;
}

/**
 * Parse alignment properties that SheetJS does not expose consistently from
 * `cellXfs`. In particular, xlsx-js-style currently drops `shrinkToFit` while
 * reading and writing; keeping the raw xf-indexed supplement closes that gap.
 */
export function parseCellXfStyleSupplements(
  stylesXml: string,
): XlsxParsedCellStyle[] {
  const block = firstElementBody(stylesXml, 'cellXfs');
  if (block == null) return [];
  const output: XlsxParsedCellStyle[] = [];
  const xfRe = /<(?:\w+:)?xf\b[^>]*?(?:\/\s*>|>([\s\S]*?)<\/(?:\w+:)?xf\s*>)/gi;
  let match: RegExpExecArray | null;
  while ((match = xfRe.exec(block)) !== null) {
    const style: XlsxParsedCellStyle = {};
    const body = match[1] ?? '';
    const alignment = /<(?:\w+:)?alignment\b([^>]*?)\/?\s*>/i.exec(body);
    if (alignment) applyParsedAlignment(parseXmlAttributes(alignment[1] ?? ''), style);
    output.push(style);
  }
  return output;
}

function resolveNumFmtCode(numFmtId: number | undefined, workbookStyles: unknown): string | undefined {
  if (numFmtId == null || numFmtId === 0) return undefined;
  const styles = workbookStyles as { NumberFmt?: unknown[] } | null | undefined;
  const numberFormats = styles?.NumberFmt;
  if (Array.isArray(numberFormats)) {
    const direct = numberFormats[numFmtId] as unknown;
    if (typeof direct === 'string' && direct) return direct;
    if (direct && typeof direct === 'object') {
      const item = direct as Record<string, unknown>;
      const code = item.formatCode ?? item.fmt;
      if (typeof code === 'string' && code) return code;
    }
    for (const raw of numberFormats) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      if ((item.numFmtId ?? item.id) !== numFmtId) continue;
      const code = item.formatCode ?? item.fmt;
      if (typeof code === 'string' && code) return code;
    }
  }
  return BUILTIN_NUM_FMTS[numFmtId];
}

function parsedBorderSide(side: RawBorderSide | undefined): XlsxParsedBorderSide | undefined {
  if (!side?.style) return undefined;
  const color = xlsxColorToHex(side.color);
  return color ? { style: side.style, color } : { style: side.style };
}

function parsedBorders(border: RawBorder | undefined): XlsxParsedBorders | undefined {
  if (!border) return undefined;
  const result: XlsxParsedBorders = {};
  const top = parsedBorderSide(border.top);
  const bottom = parsedBorderSide(border.bottom);
  const left = parsedBorderSide(border.left);
  const right = parsedBorderSide(border.right);
  if (top) result.top = top;
  if (bottom) result.bottom = bottom;
  if (left) result.left = left;
  if (right) result.right = right;
  return Object.keys(result).length > 0 ? result : undefined;
}

export function resolveParsedStyle(
  styleIndex: number,
  workbookStyles: unknown,
  borders: RawBorder[],
  cellXfSupplements: ParsedCellXfStyleSupplements = [],
): XlsxParsedCellStyle {
  const styles = workbookStyles as {
    CellXf?: Array<Record<string, unknown>>;
    Fonts?: Array<Record<string, unknown>>;
    Fills?: Array<Record<string, unknown>>;
  } | null | undefined;
  const xf = styles?.CellXf?.[styleIndex];
  const supplement = cellXfSupplements[styleIndex];
  if (!xf) return cloneStyle(supplement);
  const result: XlsxParsedCellStyle = {};

  const fontId = Number(xf.fontId ?? xf.fontid);
  const font = Number.isFinite(fontId) ? styles?.Fonts?.[fontId] : undefined;
  if (font) {
    if (font.bold || font.b) result.bold = true;
    if (font.italic || font.i) result.italic = true;
    if (font.underline || font.u) result.underline = true;
    if (font.strike || font.s) result.strike = true;
    if (typeof font.sz === 'number') result.fontSize = font.sz;
    if (typeof font.name === 'string' && font.name) result.fontFamily = font.name;
    const color = xlsxColorToHex(font.color);
    if (color) result.color = color;
  }

  const fillId = Number(xf.fillId ?? xf.fillid);
  const fill = Number.isFinite(fillId) ? styles?.Fills?.[fillId] : undefined;
  const pattern = fill?.patternType;
  if (
    fill && typeof pattern === 'string' &&
    pattern !== 'none' && pattern !== 'gray125' && pattern !== 'lightGray'
  ) {
    const color = xlsxColorToHex(fill.fgColor) ?? xlsxColorToHex(fill.bgColor);
    if (color && color !== '#000000') result.backgroundColor = color;
  }

  const borderId = Number(xf.borderId ?? xf.borderid);
  const border = Number.isFinite(borderId) ? parsedBorders(borders[borderId]) : undefined;
  if (border) result.borders = border;

  applyParsedAlignment(xf.alignment as Record<string, unknown> | undefined, result);

  if (supplement) Object.assign(result, supplement);

  const numFmtId = Number(xf.numFmtId ?? xf.numFmtid);
  const numberFormat = resolveNumFmtCode(Number.isFinite(numFmtId) ? numFmtId : undefined, styles);
  if (numberFormat) result.numberFormat = numberFormat;
  return result;
}

function cloneStyle(style: XlsxParsedCellStyle | undefined): XlsxParsedCellStyle {
  if (!style) return {};
  return {
    ...style,
    borders: style.borders
      ? {
          ...style.borders,
          top: style.borders.top ? { ...style.borders.top } : undefined,
          bottom: style.borders.bottom ? { ...style.borders.bottom } : undefined,
          left: style.borders.left ? { ...style.borders.left } : undefined,
          right: style.borders.right ? { ...style.borders.right } : undefined,
        }
      : undefined,
  };
}

function parseDirectStyle(source: unknown): XlsxParsedCellStyle {
  if (!source || typeof source !== 'object') return {};
  const raw = source as Record<string, unknown>;
  const result: XlsxParsedCellStyle = {};
  const fill = (raw.fill ?? raw) as Record<string, unknown>;
  if (
    typeof fill.patternType === 'string' &&
    fill.patternType !== 'none' && fill.patternType !== 'gray125'
  ) {
    const color = xlsxColorToHex(fill.fgColor) ?? xlsxColorToHex(fill.bgColor);
    if (color && color !== '#000000') result.backgroundColor = color;
  }
  const font = raw.font as Record<string, unknown> | undefined;
  if (font) {
    if (font.bold) result.bold = true;
    if (font.italic) result.italic = true;
    if (font.underline) result.underline = true;
    if (font.strike) result.strike = true;
    if (typeof font.sz === 'number') result.fontSize = font.sz;
    if (typeof font.name === 'string') result.fontFamily = font.name;
    const color = xlsxColorToHex(font.color);
    if (color) result.color = color;
  }
  applyParsedAlignment(raw.alignment as Record<string, unknown> | undefined, result);
  return result;
}

export interface ParsedStyleCollection {
  styles: Map<XlsxCellKey, XlsxParsedCellStyle>;
  rowStyles: Map<number, XlsxParsedCellStyle>;
  columnStyles: XlsxParsedColumnStyle[];
  references: ParsedStyleReferences;
}

export function buildParsedStyles(
  worksheet: XLSX.WorkSheet,
  sheetXml: string | null,
  workbookStyles: unknown,
  borders: RawBorder[],
  merges: XLSX.Range[],
  cellXfSupplements: ParsedCellXfStyleSupplements = [],
): ParsedStyleCollection {
  const references = sheetXml
    ? parseStyleReferences(sheetXml)
    : { cells: new Map<XlsxCellKey, number>(), rows: new Map<number, number>(), columns: [] };
  const styles = new Map<XlsxCellKey, XlsxParsedCellStyle>();
  const rowStyles = new Map<number, XlsxParsedCellStyle>();
  const columnStyles: XlsxParsedColumnStyle[] = [];

  references.cells.forEach((styleIndex, key) => {
    const style = resolveParsedStyle(styleIndex, workbookStyles, borders, cellXfSupplements);
    if (Object.keys(style).length > 0) styles.set(key, style);
  });
  references.rows.forEach((styleIndex, row) => {
    const style = resolveParsedStyle(styleIndex, workbookStyles, borders, cellXfSupplements);
    if (Object.keys(style).length > 0) rowStyles.set(row, style);
  });
  for (const ref of references.columns) {
    const style = resolveParsedStyle(ref.styleIndex, workbookStyles, borders, cellXfSupplements);
    if (Object.keys(style).length > 0) columnStyles.push({ min: ref.min, max: ref.max, style });
  }

  // Non-OOXML inputs have no raw style-index map; retain SheetJS's direct styles.
  if (!sheetXml) {
    for (const address of Object.keys(worksheet)) {
      if (address.startsWith('!')) continue;
      const cell = worksheet[address];
      if (!cell?.s) continue;
      try {
        const decoded = XLSX.utils.decode_cell(address);
        const style = parseDirectStyle(cell.s);
        if (Object.keys(style).length > 0) styles.set(`${decoded.r}:${decoded.c}`, style);
      } catch {
        // Ignore non-cell worksheet keys.
      }
    }
  }

  // cell.z is more reliable than workbook.Styles.NumberFmt for custom formats.
  for (const address of Object.keys(worksheet)) {
    if (address.startsWith('!')) continue;
    const cell = worksheet[address];
    if (!cell || typeof cell.z !== 'string' || !cell.z || cell.z === 'General') continue;
    try {
      const decoded = XLSX.utils.decode_cell(address);
      const key: XlsxCellKey = `${decoded.r}:${decoded.c}`;
      const style = cloneStyle(styles.get(key));
      style.numberFormat = cell.z;
      styles.set(key, style);
    } catch {
      // Ignore malformed cell keys.
    }
  }

  const effectiveStyle = (row: number, col: number): XlsxParsedCellStyle | undefined => {
    const direct = styles.get(`${row}:${col}`);
    if (direct) return direct;
    const rowStyle = rowStyles.get(row);
    if (rowStyle) return rowStyle;
    return columnStyles.find((entry) => col >= entry.min && col <= entry.max)?.style;
  };

  // Preserve the visual border around a merged region on its origin cell.
  for (const merge of merges) {
    const originKey: XlsxCellKey = `${merge.s.r}:${merge.s.c}`;
    const origin = cloneStyle(effectiveStyle(merge.s.r, merge.s.c));
    const bordersOut: XlsxParsedBorders = { ...(origin.borders ?? {}) };
    const topRight = effectiveStyle(merge.s.r, merge.e.c);
    const bottomLeft = effectiveStyle(merge.e.r, merge.s.c);
    const bottomRight = effectiveStyle(merge.e.r, merge.e.c);
    if (topRight?.borders?.right) bordersOut.right = { ...topRight.borders.right };
    if (bottomLeft?.borders?.bottom) bordersOut.bottom = { ...bottomLeft.borders.bottom };
    if (!bordersOut.right && bottomRight?.borders?.right) bordersOut.right = { ...bottomRight.borders.right };
    if (!bordersOut.bottom && bottomRight?.borders?.bottom) bordersOut.bottom = { ...bottomRight.borders.bottom };
    if (Object.keys(bordersOut).length > 0) origin.borders = bordersOut;
    if (Object.keys(origin).length > 0) styles.set(originKey, origin);
  }

  return { styles, rowStyles, columnStyles, references };
}
